import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { productAPI } from '../services/api';
import * as repo from '../sync/repo';
import db from '../db/localdb';
import { useToast } from '../ToastContext';
import { useAuth } from '../context/AuthContext';
import BackButton from '../components/BackButton';
import MoneyInput from '../components/MoneyInput';
import { unitLabel } from '../utils/unit';

export default function Inventory() {
  const { business } = useAuth();
  const { toast }    = useToast();
  const navigate     = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [showAdd,  setShowAdd]  = useState(false);
  const [newProd,  setNewProd]  = useState({ name: '', sku: '', unit: 'unit', sale_price: '', reorder_level: '' });
  const [restockId, setRestockId] = useState(null);
  const [restockQty, setRestockQty] = useState('');
  const [restockCost, setRestockCost] = useState('');
  const [produceId, setProduceId]   = useState(null);
  const [produceQty, setProduceQty] = useState('');

  const load = () => productAPI.list()
    .then(r => setProducts(r.data.products || []))
    .catch(async (err) => {
      if (err.response) return;
      // No connection: show the on-device copy (including products added or
      // restocked while offline).
      const bid = business?.id;
      if (!bid) return;
      const rows = await db.products.where('business_id').equals(bid).filter(p => !p.voided_at).toArray();
      rows.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setProducts(rows);
    })
    .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const result = await repo.createProduct({ ...newProd, sale_price: Number(newProd.sale_price), reorder_level: newProd.reorder_level ? Number(newProd.reorder_level) : null });
      toast(result._offline ? 'Product queued (offline)' : 'Product added', 'success');
      setShowAdd(false);
      setNewProd({ name: '', sku: '', unit: 'unit', sale_price: '', reorder_level: '' });
      load();
    } catch (err) { toast(err.response?.data?.error || 'Failed', 'error'); }
  };

  const handleRestock = async (e) => {
    e.preventDefault();
    try {
      const result = await repo.createPurchase({ product_id: restockId, qty: Number(restockQty), unit_cost: Number(restockCost) });
      toast(result._offline ? 'Restock queued (offline)' : 'Stock updated', 'success');
      setRestockId(null); setRestockQty(''); setRestockCost('');
      load();
    } catch (err) { toast(err.response?.data?.error || 'Failed', 'error'); }
  };

  const handleOwnProduce = async (e) => {
    e.preventDefault();
    try {
      const result = await repo.createOwnProduce({ product_id: produceId, qty: Number(produceQty) });
      toast(result._offline ? 'Own produce queued (offline)' : 'Stock added', 'success');
      setProduceId(null); setProduceQty('');
      load();
    } catch (err) { toast(err.response?.data?.error || 'Failed', 'error'); }
  };

  const fmt = (n) => new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

  if (loading) return <div className="page"><p className="text-muted">Loading…</p></div>;

  return (
    <div className="page">
      <BackButton />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h1 className="page-title" style={{ margin: 0 }}>Inventory</h1>
        <button className="btn-primary" onClick={() => setShowAdd(!showAdd)}>+ Add</button>
      </div>

      {showAdd && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <form onSubmit={handleCreate}>
            <div className="form-group"><label>Product name *</label><input value={newProd.name} onChange={e => setNewProd(p => ({ ...p, name: e.target.value }))} required /></div>
            <div className="form-group">
              <label>SKU / product code (optional)</label>
              <input value={newProd.sku} placeholder="e.g. HNY500" onChange={e => setNewProd(p => ({ ...p, sku: e.target.value }))} />
              <div className="text-muted" style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>An optional short code to find this product faster. Leave blank if unsure.</div>
            </div>
            <div className="form-group"><label>Unit</label><input value={newProd.unit} onChange={e => setNewProd(p => ({ ...p, unit: e.target.value }))} /></div>
            <div className="form-group"><label>Sale price ({business?.currency})</label><MoneyInput value={newProd.sale_price} onChange={e => setNewProd(p => ({ ...p, sale_price: e.target.value }))} required /></div>
            <div className="form-group"><label>Reorder level</label><input type="number" step="0.001" value={newProd.reorder_level} onChange={e => setNewProd(p => ({ ...p, reorder_level: e.target.value }))} /></div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="submit" className="btn-primary">Save</button>
              <button type="button" className="btn-ghost" onClick={() => setShowAdd(false)}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {restockId && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <h3 style={{ marginBottom: '0.75rem', fontSize: '0.95rem' }}>Restock — {products.find(p => p.id === restockId)?.name}</h3>
          <form onSubmit={handleRestock}>
            <div className="form-group"><label>Qty *</label><input type="number" step="0.001" value={restockQty} onChange={e => setRestockQty(e.target.value)} required /></div>
            <div className="form-group"><label>Unit cost</label><MoneyInput value={restockCost} onChange={e => setRestockCost(e.target.value)} /></div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="submit" className="btn-primary">Restock</button>
              <button type="button" className="btn-ghost" onClick={() => setRestockId(null)}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {produceId && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <h3 style={{ marginBottom: '0.35rem', fontSize: '0.95rem' }}>Own produce — {products.find(p => p.id === produceId)?.name}</h3>
          <p className="text-muted" style={{ fontSize: '0.78rem', marginBottom: '0.75rem' }}>
            For stock you produced yourself (not bought). Adds quantity at no cost — record any growing costs under Expenses.
          </p>
          <form onSubmit={handleOwnProduce}>
            <div className="form-group"><label>Qty produced *</label><input type="number" step="0.001" value={produceQty} onChange={e => setProduceQty(e.target.value)} required autoFocus /></div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="submit" className="btn-primary">Add stock</button>
              <button type="button" className="btn-ghost" onClick={() => setProduceId(null)}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {products.length === 0 && <p className="text-muted">No products yet. Add your first product above.</p>}

      {products.map(p => {
        // Depleted (0 or below) takes priority over low stock. Low stock matches
        // the dashboard banner / backend low-stock query.
        const depleted = !p.is_service && Number(p.stock_qty) <= 0;
        const lowStock = !depleted && p.reorder_level != null && p.reorder_level !== '' &&
          Number(p.stock_qty) <= Number(p.reorder_level);
        return (
        <div key={p.id} className="card" style={{ marginBottom: '0.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => navigate(`/inventory/${p.id}`)}>
            <div style={{ fontWeight: 600 }}>
              {p.name}
              {!p.is_service && p.oversold && <span style={{ color: 'var(--color-danger)', fontSize: '0.75rem', marginLeft: '0.4rem' }}>⚠ Oversold</span>}
              {!p.oversold && depleted && <span style={{ color: 'var(--color-danger)', fontSize: '0.75rem', marginLeft: '0.4rem' }}>⚠ Depleted</span>}
              {lowStock && <span style={{ color: 'var(--color-warning)', fontSize: '0.75rem', marginLeft: '0.4rem' }}>⚠ Low stock</span>}
            </div>
            <div className="text-muted">
              {p.sku ? `SKU: ${p.sku} · ` : ''}{unitLabel(p.unit) ? `${unitLabel(p.unit)} · ` : ''}
              {p.is_service ? 'Service — stock not tracked' : <>Stock:{' '}
                <span style={(depleted || p.oversold) ? { color: 'var(--color-danger)', fontWeight: 600 } : lowStock ? { color: 'var(--color-warning)', fontWeight: 600 } : undefined}>{parseFloat(Number(p.stock_qty).toFixed(3))}</span>
              </>}
            </div>
            <div style={{ fontSize: '0.9rem', marginTop: '0.2rem' }}>{business?.currency} {fmt(p.sale_price)}</div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {!p.is_service && (
              <button className="btn-ghost" style={{ padding: '0.4rem 0.7rem', fontSize: '0.8rem' }} onClick={() => { setRestockId(p.id); setRestockQty(''); setRestockCost(''); setProduceId(null); }}>Restock</button>
            )}
            {!p.is_service && (
              <button className="btn-ghost" style={{ padding: '0.4rem 0.7rem', fontSize: '0.8rem' }} onClick={() => { setProduceId(p.id); setProduceQty(''); setRestockId(null); }}>Own produce</button>
            )}
            <Link to={`/sell?product=${p.id}`} className="btn-primary" style={{ padding: '0.4rem 0.7rem', fontSize: '0.8rem', borderRadius: '6px' }}>Sell</Link>
          </div>
        </div>
        );
      })}
    </div>
  );
}
